<template>
    <v-form
        ref="form"
        v-model="isValid"
        @submit.prevent="save()">
        <v-card>
            <v-card-title>
                <span class="headline">{{ isEdit ? "Edit" : "Add" }} {{ entity.singleTitle }}</span>
            </v-card-title>

            <v-card-text>
                <form-text-field
                    v-model="item.scopeOfWorks"
                    :field="scopeOfWorksField"
                    validate-on-blur
                    @save="save" />
                <form-search-select
                    v-if="!!proposalTemplateField && !isEdit"
                    v-model="item.proposalTemplateId"
                    :field="proposalTemplateField"
                    validate-on-blur
                    @save="save" />
                <form-search-select
                    v-if="!!departmentIdField"
                    v-model="item.departmentId"
                    :field="departmentIdField"
                    validate-on-blur
                    @save="save" />
                <schema-search-select
                    :value="clientId"
                    entity-key="client"
                    search-when-blank
                    label="Client"
                    @input="onClientSelected" />
                <form-search-select
                    v-if="isSiteFieldVisible"
                    v-model="item.siteId"
                    :field="siteIdField"
                    validate-on-blur
                    :filter="siteFilter"
                    @save="save"
                    @input="onSiteSelected" />

                <div v-if="isSiteContactVisible">
                    <form-search-select
                        v-model="item.recipientContactId"
                        :field="recipientContactIdField"
                        validate-on-blur
                        label="Site Contact"
                        :filter="siteContactFilter"
                        :fixed-values="siteContactFixedValues"
                        @save="save" />
                </div>

                <form-number
                    v-if="!!estimateField"
                    v-model="item.estimate"
                    :field="estimateField"
                    validate-on-blur
                    @save="save" />
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4">
                <app-button
                    v-if="canDelete"
                    color="error"
                    text
                    @click="deleteItem">
                    Delete
                </app-button>
                <v-spacer />
                <app-button
                    v-if="canCancel"
                    color="secondary"
                    text
                    @click="$emit('cancel')">
                    Cancel
                </app-button>
                <app-button
                    v-if="canSave"
                    type="submit"
                    text
                    :disabled="isSaving || !item.recipientContactId">
                    Save
                </app-button>
            </v-card-actions>

            <v-progress-linear v-if="isSaving" indeterminate color="blue" />
        </v-card>
    </v-form>
</template>

<script>
import formMixin from "@/features/schemas/mixins/formMixin";
import {get, save} from '@/features/schemas/services/schemaApi';
import { equal, and, or } from "@/services/filtering"
import FormTextField from "@/features/schemas/formFields/FormTextField.vue";

export default {
    components: {
        FormTextField,
        FormSearchSelect: () => import("@/features/schemas/formFields/FormSearchSelect.vue"),
        FormNumber: () => import("@/features/schemas/formFields/FormNumber.vue"),
        SchemaSearchSelect: () => import("@/features/schemas/components/SchemaSearchSelect.vue"),
    },
    mixins: [formMixin],
    types: [
        "proposal"
    ],
    data() {
        return {
            clientId: null,
            isLoading: false,
        };
    },

    computed: {
        isSiteFieldVisible() {
            return this.isEdit || ((this.clientId || this.item.siteId) && this.siteIdField);
        },

        isSiteContactVisible() {
            return !!this.recipientContactIdField && !!this.item.siteId;
        },

        siteIdField() {
            return this.fields.find(f => f.key === "siteId");
        },
        departmentIdField() {
            return this.fields.find(f => f.key === "departmentId");
        },
        proposalTemplateField() {
            return this.fields.find(f => f.key === "proposalTemplateId");
        },
        recipientContactIdField() {
            return this.fields.find(f => f.key === "recipientContactId");
        },
        estimateField() {
            return this.fields.find(f => f.key === "estimate");
        },
        scopeOfWorksField() {
            return this.fields.find(f => f.key === "scopeOfWorks");
        },
        siteFilter() {
            return this.clientId ? equal("clientId", this.clientId) : null;
        },

        siteContactFilter() {
            const filters = [
                equal("hasEmail", true, "bool"),
                equal("siteId", this.item.siteId),
            ];

            const base = and(filters);

            if (this.item.recipientContactId) {
                return or([base, equal("id", this.item.recipientContactId)]);
            }
            return base;
        },

        canSave() {
            return this.isEdit ?
                this.canEdit :
                this.canAdd;
        },

        siteContactFixedValues() {
            return this.item.siteId ? { siteId: this.item.siteId } : {};
        },

        fixedContactValues() {
            return this.siteContactFixedValues;
        },
    },

    async mounted() {
        if (this.item.clientId) {
            this.clientId = this.item.clientId;
        }

        if (this.item.siteId) {
            await this.loadSiteData(this.item.siteId);
        }
    },

    methods: {
        async save() {
            if (this.isSaving || this.form && !this.form.validate()) {
                return;
            }

            const model = { ...this.item, ...this.fixedContactValues };
            if (model.siteId) {
                model.clientId = null;
            }

            this.isSaving = true;
            try {
                this.item = await save(this.entityKey, this.scope, model, true);
                this.$emit('input', this.item);
            } finally {
                this.isSaving = false;
            }
        },

        async loadSiteData(siteId) {
            if (!siteId) return;

            this.isLoading = true;
            try {
                const site = await get("site", this.scope, siteId);

                if (site.clientId && !this.clientId) {
                    this.clientId = site.clientId;
                }
            } finally {
                this.isLoading = false;
            }
        },

        async onClientSelected(clientId) {
            const previousClientId = this.clientId;

            this.clientId = clientId;
            this.$set(this.item, 'clientId', clientId);

            if (previousClientId && previousClientId !== clientId) {
                this.$set(this.item, 'siteId', null);
                this.$set(this.item, 'recipientContactId', null);
            }
        },

        async onSiteSelected(siteId) {
            if (this.item.recipientContactId) {
                this.$set(this.item, 'recipientContactId', null);
            }

            if (siteId) {
                await this.loadSiteData(siteId);
            }
        }
    }
}
</script>
